import { QBS_STATUS_TEXT } from '@exam-management/components';
import { QBankStructureStatus, QBankStructureType } from '@exam-management/graphql';
import {
  Button,
  Flex,
  FilterListOutlinedIcon,
  Collapse,
  Box,
  FormSearchMultipleSelection,
  FormCheckbox,
  Divider,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Rotate,
  ExpandMoreRoundedIcon,
} from '@shared/components';
import { capitalize } from '@shared/utils';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import QBankStructureListSearchBox from '../../components/qbank-structure-list-search-box/qbank-structure-list-search-box';
import { useQBankStructureSearchParams } from '../../hooks';

import GroupSelection from './group-selection';
import type { QBankStructureListFilterForm, StatusOption } from './types';

function mapParamToQBankStatus(statusName: QBankStructureStatus[]) {
  return statusName.map<StatusOption>((oldStatus) => {
    return { label: capitalize(oldStatus), value: oldStatus };
  });
}

const statusOptionsOrder = [
  QBankStructureStatus.Draft,
  QBankStructureStatus.Published,
  QBankStructureStatus.Unpublished,
  QBankStructureStatus.Archived,
];

export default function QBankStructureListFilterSection() {
  const [{ statuses, types }, setQBankStructureSearchParams] = useQBankStructureSearchParams();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const formContext = useForm<QBankStructureListFilterForm>({
    mode: 'onTouched',
    defaultValues: {
      status: statuses ? mapParamToQBankStatus(statuses) : [],
      [QBankStructureType.Main]: types.includes(QBankStructureType.Main),
      [QBankStructureType.Mock]: types.includes(QBankStructureType.Mock),
    },
  });

  const typeOptions = Object.values(QBankStructureType);
  const statusOptions: StatusOption[] = statusOptionsOrder.map((status) => ({
    label: QBS_STATUS_TEXT[status],
    value: status,
  }));

  const toggleFilterCollapse = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleResetFilter = () => {
    formContext.reset({
      status: [],
      [QBankStructureType.Main]: false,
      [QBankStructureType.Mock]: false,
    });
  };

  const handleSubmitForm = () => {
    const mappedStatusList = formContext.getValues('status').map((statusObj) => statusObj.value);

    const typeFilterList = typeOptions
      .map((typeOption) => {
        if (formContext.getValues(typeOption)) {
          return typeOption;
        }
      })
      .filter((option) => option !== undefined);

    setQBankStructureSearchParams({
      statuses: mappedStatusList,
      types: typeFilterList,
      page: null,
    });
  };

  return (
    <Box>
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}
      >
        <Box
          sx={{
            maxWidth: 260,
            width: 1,
          }}
        >
          <GroupSelection />
        </Box>
        <Flex sx={{ gap: (theme) => theme.spacing(2) }}>
          <QBankStructureListSearchBox />
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            startIcon={<FilterListOutlinedIcon />}
            endIcon={
              <Rotate in={isExpanded}>
                <ExpandMoreRoundedIcon />
              </Rotate>
            }
            onClick={toggleFilterCollapse}
            sx={{
              minWidth: 'max-content',
            }}
          >
            Filter
          </Button>
        </Flex>
      </Flex>

      <Collapse in={isExpanded} timeout='auto' unmountOnExit sx={{ gridColumnEnd: 'span 4' }}>
        <FormProvider {...formContext}>
          <Form onSubmit={formContext.handleSubmit(handleSubmitForm)}>
            <Flex
              sx={(theme) => ({
                mt: theme.spacing(6),
                p: theme.spacing(4),
                borderRadius: theme.shape.rounded(2),
                border: '1px solid',
                borderColor: 'grey.300',
                flexDirection: 'column',
                gap: theme.spacing(4),
              })}
            >
              <Flex sx={{ gap: (theme) => theme.spacing(4) }}>
                <FormSearchMultipleSelection
                  key='status'
                  name='status'
                  label='Status'
                  noOptionsText='No result found'
                  options={statusOptions}
                  placeholder={formContext.watch().status.length == 0 ? 'Select Status' : undefined}
                  sx={{
                    flexBasis: '35%',
                  }}
                />
                <FormControl component='fieldset'>
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      alignContent: 'center',
                      gap: (theme) => theme.spacing(1),
                    }}
                  >
                    <FormLabel component='legend'>Type</FormLabel>
                    <FormGroup row>
                      {typeOptions.map((typeOption) => {
                        return (
                          <FormCheckbox
                            key={`qbank-type-${typeOption}`}
                            name={typeOption}
                            label={capitalize(typeOption)}
                          />
                        );
                      })}
                    </FormGroup>
                  </Flex>
                </FormControl>
              </Flex>

              <Divider />

              <Flex sx={{ gap: (theme) => theme.spacing(2), justifyContent: 'end' }}>
                <Button
                  color='secondary'
                  variant='outlined'
                  onClick={handleResetFilter}
                  sx={{ width: 100 }}
                >
                  Reset
                </Button>
                <Button type='submit' sx={{ width: 100 }}>
                  Apply
                </Button>
              </Flex>
            </Flex>
          </Form>
        </FormProvider>
      </Collapse>
    </Box>
  );
}
